import type { EvolutionView } from '../../shared/protocol.ts';
import { $, restartAnimation } from '../common/dom.ts';
import { EVOLVE_CHARGE_S, type SoundBoard } from './sound.ts';

/** L'ancienne forme, seule, avant que la lumière ne la prenne. */
const INTRO_MS = 1100;
/** Transformation : les deux silhouettes alternent de plus en plus vite, au rythme du trille. */
const CHARGE_MS = EVOLVE_CHARGE_S * 1000;
/** Après l'éclat, le temps de voir la nouvelle forme avant de pouvoir continuer. */
const REVEAL_MS = 900;
/** Alternance des silhouettes : de lente à presque continue. */
const SWAP_SLOWEST_MS = 460;
const SWAP_FASTEST_MS = 45;
const SPARKS = 28;
/** Musique baissée pendant la scène, pour laisser la place au trille et à la fanfare. */
const MUSIC_DUCK = 0.25;

/**
 * Mise en scène d'une évolution : l'ancienne forme devient une silhouette de lumière qui alterne, de plus en plus vite,
 * avec celle de la nouvelle, pendant que des étincelles convergent ; puis un éclat blanc révèle la nouvelle forme.
 */
export class EvolutionScene {
  private readonly root = $('#evolution');
  private readonly stage = $('.evolution-stage', this.root);
  private readonly from = $<HTMLImageElement>('.evo-from', this.root);
  private readonly to = $<HTMLImageElement>('.evo-to', this.root);
  private readonly sparks = $('.evo-sparks', this.root);
  private readonly text = $('.evolution-text', this.root);
  private readonly closeButton = $<HTMLButtonElement>('.evolution-close', this.root);
  private readonly sounds: SoundBoard;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private onDone: (() => void) | null = null;

  constructor(sounds: SoundBoard) {
    this.sounds = sounds;
    this.closeButton.addEventListener('click', () => this.close());
    this.sparks.replaceChildren(...Array.from({ length: SPARKS }, (_, i) => {
      const spark = document.createElement('i');
      spark.style.setProperty('--a', `${(360 / SPARKS) * i + Math.random() * 8}deg`);
      spark.style.setProperty('--d', `${120 + Math.random() * 140}px`);
      spark.style.setProperty('--delay', `${-Math.random() * 1.2}s`);
      return spark;
    }));
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Joue les évolutions l'une après l'autre, puis `onDone`. */
  playAll(evolutions: readonly EvolutionView[], onDone: () => void): void {
    const [first, ...rest] = evolutions;
    if (!first) return onDone();
    this.play(first, () => this.playAll(rest, onDone));
  }

  play(evolution: EvolutionView, onDone?: () => void): void {
    this.clearTimers();
    this.onDone = onDone ?? null;
    this.from.src = evolution.fromImage;
    this.to.src = evolution.toImage;
    this.text.textContent = `Quoi ? ${evolution.fromName} évolue !`;
    this.closeButton.hidden = true;
    this.stage.classList.remove('swap');
    this.root.classList.remove('charging', 'burst');
    this.root.hidden = false;
    restartAnimation(this.root, 'evolving');
    this.sounds.duck('music', MUSIC_DUCK);

    this.later(INTRO_MS, () => {
      this.root.classList.add('charging');
      this.sounds.play('evolve');
      this.scheduleSwaps();
    });
    this.later(INTRO_MS + CHARGE_MS, () => {
      this.stage.classList.remove('swap');
      this.root.classList.remove('charging');
      restartAnimation(this.root, 'burst');
      this.sounds.play('evolved');
      this.text.textContent = `Félicitations ! ${evolution.fromName} a évolué en ${evolution.toName} !`;
      restartAnimation(this.text, 'pop');
    });
    this.later(INTRO_MS + CHARGE_MS + REVEAL_MS, () => {
      this.closeButton.hidden = false;
      this.closeButton.focus();
    });
  }

  close(): void {
    this.clearTimers();
    this.root.hidden = true;
    this.root.classList.remove('evolving', 'charging', 'burst');
    this.sounds.duck('music', 1);
    const done = this.onDone;
    this.onDone = null;
    done?.();
  }

  /** Les silhouettes alternent à intervalles de plus en plus courts (accélération en douceur, puis frénétique). */
  private scheduleSwaps(): void {
    let elapsed = 0;
    while (elapsed < CHARGE_MS) {
      const progress = elapsed / CHARGE_MS;
      elapsed += SWAP_SLOWEST_MS - (SWAP_SLOWEST_MS - SWAP_FASTEST_MS) * Math.sqrt(progress);
      if (elapsed < CHARGE_MS) this.later(elapsed, () => this.stage.classList.toggle('swap'));
    }
  }

  private later(ms: number, action: () => void): void {
    this.timers.push(setTimeout(action, ms));
  }

  private clearTimers(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }
}
